import { jsx, jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import "../../../node_modules/.pnpm/katex@0.10.2/node_modules/katex/dist/katex.js";
/* empty css                                                                             */
import { useTranslation } from "react-i18next";
import { QUIZ_INTERACTIVE_STYLES } from "../shared/quiz-styles.js";
import { useQuizStyleVariant } from "../shared/quiz-style-context.js";
import { QuizResultToggle } from "../shared/quiz-result-toggle.js";
import { SubmitButton } from "../shared/submit-button.js";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import "../../../context/DevToolsContext.js";
import { QuizHeader } from "../shared/quiz-header.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { formatProblem } from "../../../utils/quiz-parser.js";
import { useFillBlankQuiz } from "../hooks/use-fill-blank-quiz.js";
import { AiqKeypad } from "../shared/aiq-keypad.js";
const BLANK_INPUT_MIN_WIDTH_PX = 48;
const BLANK_INPUT_NARROW_CHARACTER_WIDTH_PX = 16;
const BLANK_INPUT_WIDE_CHARACTER_WIDTH_PX = 24;
const BLANK_INPUT_HORIZONTAL_PADDING_PX = 32;
const BLANK_INPUT_BORDER_WIDTH_PX = 4;
const AIQ_BLANK_INPUT_MIN_WIDTH_PX = 40;
const AIQ_BLANK_INPUT_CHARACTER_WIDTH_PX = 16;
const AIQ_BLANK_INPUT_HORIZONTAL_PADDING_PX = 16;
function isWideCharacter(character) {
  const codePoint = character.codePointAt(0) ?? 0;
  return codePoint >= 4352 && codePoint <= 4447 || codePoint >= 11904 && codePoint <= 42191 || codePoint >= 44032 && codePoint <= 55203 || codePoint >= 63744 && codePoint <= 64255 || codePoint >= 65040 && codePoint <= 65135 || codePoint >= 65281 && codePoint <= 65376 || codePoint >= 65504 && codePoint <= 65510 || codePoint >= 127744 && codePoint <= 129791 || codePoint >= 131072 && codePoint <= 262141;
}
function getBlankInputWidth(value) {
  const contentWidth = Array.from(value).reduce(
    (width, character) => width + (isWideCharacter(character) ? BLANK_INPUT_WIDE_CHARACTER_WIDTH_PX : BLANK_INPUT_NARROW_CHARACTER_WIDTH_PX),
    0
  );
  return `${Math.max(
    BLANK_INPUT_MIN_WIDTH_PX,
    contentWidth + BLANK_INPUT_HORIZONTAL_PADDING_PX + BLANK_INPUT_BORDER_WIDTH_PX
  )}px`;
}
function getLcBlankInputWidth(value) {
  if (value.length === 0) return "40px";
  const contentWidth = Array.from(value).reduce(
    (width, character) => width + (isWideCharacter(character) ? BLANK_INPUT_WIDE_CHARACTER_WIDTH_PX : BLANK_INPUT_NARROW_CHARACTER_WIDTH_PX),
    0
  );
  return `${Math.max(
    80,
    contentWidth + BLANK_INPUT_HORIZONTAL_PADDING_PX + BLANK_INPUT_BORDER_WIDTH_PX
  )}px`;
}
function getAiqBlankInputWidth(value) {
  const contentWidth = Array.from(value).length * AIQ_BLANK_INPUT_CHARACTER_WIDTH_PX;
  return `${Math.max(
    AIQ_BLANK_INPUT_MIN_WIDTH_PX,
    contentWidth + AIQ_BLANK_INPUT_HORIZONTAL_PADDING_PX
  )}px`;
}
function FillBlankStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  useKeypad = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound: _disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  hideSolveDescription = false,
  dimChoiceText = false
}) {
  const { t } = useTranslation("quiz");
  const variant = useQuizStyleVariant();
  const isLcV3 = variant === "lc";
  const isAiq = variant === "aiq";
  const ix = QUIZ_INTERACTIVE_STYLES[variant];
  const keypadActive = useKeypad || variant === "aiq";
  const {
    answers,
    currentIndex,
    blankCount,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleBlankClick,
    handleInputChange,
    handleSubmit,
    handleKeypadInput
  } = useFillBlankQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  const renderedProblem = useMemo(() => {
    if (blankCount === 0) return [];
    return formatProblem(
      quiz.problem,
      `${quizId}-problem`,
      (index, content) => {
        var _a, _b;
        const key = content || "";
        const value = answers[index] ?? "";
        const hasValue = value.length > 0;
        const isActive = currentIndex === index;
        const isCorrect = isSolved ? ((_a = solveResult == null ? void 0 : solveResult.corrects) == null ? void 0 : _a[index]) ?? null : null;
        const solved = isSolved || isViewSolved;
        if (solved) {
          const correctAnswer = ((_b = solveResult == null ? void 0 : solveResult.correctAnswers) == null ? void 0 : _b[index]) ?? "";
          const showCorrectAnswer = shouldShowCorrectAnswers && correctAnswer !== "";
          const displayValue = showCorrectAnswer ? correctAnswer : value || "?";
          return /* @__PURE__ */ jsx(
            "span",
            {
              className: cn(
                "inline-flex items-center justify-center align-middle font-medium transition-all text-center [box-shadow:unset] pointer-events-none",
                isLcV3 ? "mx-1 h-10 rounded-[10px] border-[1.5px] px-2 text-[20px]" : isAiq ? "mx-1.5 h-11 min-w-10 rounded-[10px] border-[1.5px] px-2 text-[22px] font-bold" : "mx-2 h-12 rounded-2xl border-none px-4 text-[24px]",
                showCorrectAnswer && (isLcV3 ? "border-lc-success-500 bg-lc-success-50 text-lc-text-success" : "bg-lc-mint-200 text-lc-mint-900"),
                !showCorrectAnswer && isCorrect === true && (isLcV3 ? "border-lc-success-500 bg-lc-success-50 text-lc-text-success" : "bg-lc-mint-200 text-lc-mint-900"),
                !showCorrectAnswer && isCorrect === false && (isLcV3 ? "border-lc-error-500 bg-lc-error-50 text-lc-text-error" : "bg-lc-red-200 text-lc-red-900"),
                !showCorrectAnswer && isCorrect === null && (isLcV3 ? "border-lc-key-500 bg-[#ddf3ff] text-lc-text-primary" : "bg-lc-lightblue-200 text-gray-900")
              ),
              style: isLcV3 ? { width: getLcBlankInputWidth(displayValue) } : isAiq ? { width: getAiqBlankInputWidth(displayValue) } : void 0,
              children: displayValue
            },
            `blank-${quizId}-${index}`
          );
        }
        if (keypadActive) {
          return /* @__PURE__ */ jsx(
            "span",
            {
              "data-blank": index,
              onClick: () => handleBlankClick(index),
              className: cn(
                "inline-flex items-center justify-center align-middle font-medium text-gray-900 transition-all text-center cursor-pointer select-none",
                isLcV3 ? "mx-1 h-10 rounded-[10px] border-[1.5px] px-2 text-[20px]" : isAiq ? "mx-1.5 h-11 min-w-10 rounded-[10px] border-[1.5px] px-2 text-[22px] font-bold" : "mx-2 h-12 min-w-[56px] rounded-2xl px-4 text-[24px]",
                !isActive && !hasValue && ix.blankEmpty,
                isActive && ix.blankActive,
                isActive && hasValue && !isLcV3 && !isAiq && "[box-shadow:0px_4px_0px_rgba(85,195,255,0.3)]",
                !isActive && hasValue && ix.blankFilled
              ),
              style: isLcV3 ? { width: getLcBlankInputWidth(value) } : isAiq ? { width: getAiqBlankInputWidth(value) } : void 0,
              children: value || "?"
            },
            `blank-${quizId}-${index}`
          );
        }
        return /* @__PURE__ */ jsx(
          "input",
          {
            type: "text",
            value,
            onChange: (e) => handleInputChange(index, e.target.value, key),
            onClick: () => handleBlankClick(index),
            placeholder: "?",
            className: cn(
              "inline-flex items-center justify-center align-middle font-medium text-gray-900 transition-all text-center outline-hidden",
              isLcV3 ? "mx-1 h-10 rounded-[10px] border-[1.5px] px-2 text-[20px]" : "mx-2 h-12 rounded-2xl px-4 text-[24px]",
              !isActive && !hasValue && ix.blankEmpty,
              isActive && ix.blankActive,
              isActive && hasValue && !isLcV3 && !isAiq && "[box-shadow:0px_4px_0px_rgba(85,195,255,0.3)]",
              !isActive && hasValue && ix.blankFilled,
              "min-w-0"
            ),
            style: { width: isLcV3 ? getLcBlankInputWidth(value) : getBlankInputWidth(value) }
          },
          `blank-${quizId}-${index}`
        );
      },
      quiz.additionalImages ?? void 0
    );
  }, [blankCount, currentIndex, isSolved, isViewSolved, solveResult, shouldShowCorrectAnswers, quiz.problem, quizId, handleBlankClick, handleInputChange, answers, quiz.additionalImages, keypadActive, ix, isLcV3, isAiq]);
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-6", keypadActive && !isSolved && !isViewSolved && "pb-[304px]"), children: [
    /* @__PURE__ */ jsx(
      QuizHeader,
      {
        quizId,
        typeLabel: t("types.fillBlank.label"),
        title: quiz.title,
        difficultyLevel,
        variant: "student",
        image: quiz.image,
        imageMobile: quiz.imageMobile,
        desmos: quiz.desmos,
        vars: quiz.vars,
        dimTitleText: dimChoiceText,
        dimMedia: dimChoiceText
      }
    ),
    /* @__PURE__ */ jsx(
      "div",
      {
        "data-quiz-problem-text": true,
        className: cn(
          isLcV3 ? "text-[20px] font-medium leading-[1.45] tracking-[-0.1px] text-lc-text-primary" : isAiq ? "text-[22px] font-medium leading-normal text-lc-text-primary" : "text-2xl font-medium text-gray-900",
          dimChoiceText && "[&_.formatProblem]:opacity-[0.05] [&_.formatProblem_other]:opacity-[0.05]"
        ),
        children: renderedProblem
      }
    ),
    !isSolved && !isViewSolved && !hideSubmitButton && !keypadActive && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel
      }
    ),
    isSolved && !hideExplanation && /* @__PURE__ */ jsx(
      QuizResultToggle,
      {
        value: isShowingCorrectAnswers ? "correct-and-explanation" : "my-answer",
        onChange: (next) => setIsShowingCorrectAnswers(next === "correct-and-explanation")
      }
    ),
    isSolved && isShowingCorrectAnswers && !hideExplanation && !hideSolveDescription && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: (solveResult == null ? void 0 : solveResult.description) ?? void 0,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: false
      }
    ),
    keypadActive && blankCount > 0 && !isSolved && !isViewSolved && /* @__PURE__ */ jsx(
      AiqKeypad,
      {
        onInput: handleKeypadInput,
        onPrev: () => {
          if (currentIndex > 0) handleBlankClick(currentIndex - 1);
        },
        onNext: () => {
          if (currentIndex < blankCount - 1) handleBlankClick(currentIndex + 1);
        },
        canPrev: currentIndex > 0,
        canNext: currentIndex < blankCount - 1
      }
    )
  ] });
}
export {
  FillBlankStudent
};
//# sourceMappingURL=fill-blank-student.js.map
